/**
 * Image handling utilities
 * Consolidates image error handling and validation logic used across multiple components
 */

// Default avatar path - centralized constant
export const DEFAULT_AVATAR_PATH = '/images/default-avatar-silhouette.svg';

// Mobile-aware image size configuration
const IS_MOBILE = window.Capacitor?.isNativePlatform?.() ?? false;
export const IMAGE_CONFIG = {
  MAX_SIZE: IS_MOBILE ? 1000 : 1200,  // Lower max size on mobile to save memory
  QUALITY: IS_MOBILE ? 0.8 : 0.85,     // Lower quality on mobile to save bandwidth
  THUMBNAIL_QUALITY: IS_MOBILE ? 0.75 : 0.8
};

// Common invalid profile picture patterns that should be replaced with default
const INVALID_PROFILE_PATTERNS = [
  '/images/small_profile_avatar.png',
  'small_profile_avatar.png',
  '/images/author-avatar1.png',
  '/images/author-avatar2.png',
  'author-avatar1.png',
  'author-avatar2.png'
];

/**
 * Handle image error by setting source to default avatar
 * This is the standardized error handler for profile pictures
 * @param {Event} event - The error event from an img element
 * @param {string} fallbackSrc - Optional custom fallback image (defaults to DEFAULT_AVATAR_PATH)
 */
export function handleImageError(event, fallbackSrc = DEFAULT_AVATAR_PATH) {
  if (event && event.target) {
    event.target.src = fallbackSrc;
  }
}

/**
 * Validate profile picture URL and return default if invalid
 * Checks for common invalid patterns and returns appropriate fallback
 * @param {string} profilePicUrl - The profile picture URL to validate
 * @param {string} defaultSrc - Optional custom default image (defaults to DEFAULT_AVATAR_PATH)
 * @returns {string} Valid profile picture URL or default avatar path
 */
export function getValidProfilePicture(profilePicUrl, defaultSrc = DEFAULT_AVATAR_PATH) {
  // Return default if no URL provided
  if (!profilePicUrl) {
    return defaultSrc;
  }
  
  // Check for common invalid URLs
  if (INVALID_PROFILE_PATTERNS.some(pattern => profilePicUrl.includes(pattern))) {
    return defaultSrc;
  }
  
  return profilePicUrl;
}

/**
 * Check if a profile picture URL is valid (not in the invalid patterns list)
 * @param {string} profilePicUrl - The profile picture URL to check
 * @returns {boolean} True if the URL is valid, false if it should be replaced
 */
export function isValidProfilePicture(profilePicUrl) {
  if (!profilePicUrl) return false;
  
  return !INVALID_PROFILE_PATTERNS.some(pattern => profilePicUrl.includes(pattern));
}

/**
 * Create an image error handler function for Vue components
 * Returns a method that can be used directly in Vue component methods
 * @param {string} fallbackSrc - Optional custom fallback image
 * @returns {Function} Error handler function for Vue components
 */
export function createImageErrorHandler(fallbackSrc = DEFAULT_AVATAR_PATH) {
  return function(event) {
    handleImageError(event, fallbackSrc);
  };
}

/**
 * Get profile picture with validation and fallback
 * Combines validation and fallback logic in one function
 * @param {string} profilePicUrl - The profile picture URL
 * @param {string} defaultSrc - Optional custom default image
 * @returns {string} Validated profile picture URL
 */
export function getProfilePictureWithFallback(profilePicUrl, defaultSrc = DEFAULT_AVATAR_PATH) {
  return getValidProfilePicture(profilePicUrl, defaultSrc);
}

/**
 * Add invalid profile picture patterns to the global list
 * Allows extending the invalid patterns at runtime if needed
 * @param {string|Array<string>} patterns - Pattern(s) to add
 */
export function addInvalidProfilePattern(patterns) {
  const patternsArray = Array.isArray(patterns) ? patterns : [patterns];
  INVALID_PROFILE_PATTERNS.push(...patternsArray);
}

/**
 * Get all current invalid profile picture patterns
 * @returns {Array<string>} Copy of the invalid patterns array
 */
export function getInvalidProfilePatterns() {
  return [...INVALID_PROFILE_PATTERNS];
}

/**
 * Resize an image file to specified dimensions
 * @param {File} file - The image file to resize
 * @param {Object} options - Resize options
 * @param {number} options.maxWidth - Maximum width (default: 400)
 * @param {number} options.maxHeight - Maximum height (default: 400)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @param {string} options.format - Output format 'jpeg', 'png', 'webp' (default: 'jpeg')
 * @returns {Promise<File>} Resized image file
 */
export function resizeImage(file, options = {}) {
  const {
    maxWidth = 400,
    maxHeight = 400,
    quality = 0.8,
    format = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      // Set canvas size
      canvas.width = width;
      canvas.height = height;

      // Draw and resize image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create new File object with resized image
            const resizedFile = new File([blob], file.name, {
              type: `image/${format}`,
              lastModified: Date.now()
            });
            resolve(resizedFile);
          } else {
            reject(new Error('Failed to resize image'));
          }
        },
        `image/${format}`,
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Generate optimized profile picture (80x80 for avatars)
 * @param {File} file - The image file to resize
 * @returns {Promise<File>} Optimized profile picture file
 */
export async function createProfilePicture(file) {
  try {
    // Create optimized version for profile pictures (80x80, high quality)
    const profilePic = await resizeImage(file, { 
      maxWidth: 80, 
      maxHeight: 80, 
      quality: 0.9,
      format: 'jpeg'
    });

    return profilePic;
  } catch (error) {
    console.error('Error creating profile picture:', error);
    throw error;
  }
}

/**
 * Get file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Human readable size (e.g., "1.2 MB")
 */
export function getFileSizeString(bytes) {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Generate multiple sizes for post images
 * @param {File} file - The image file to resize
 * @param {Object} options - Resize options
 * @returns {Promise<Object>} Object with different sized versions and metadata
 */
export async function createPostImageSizes(file, options = {}) {
  const {
    thumbnailSize = 300,                // Newsfeed size
    mediumSize = 600,                   // Modal/click view size
    originalMaxSize = IMAGE_CONFIG.MAX_SIZE,  // Max original size (mobile-aware)
    quality = IMAGE_CONFIG.QUALITY      // JPEG quality (mobile-aware)
  } = options;

  try {
    // Get original image dimensions
    const originalDimensions = await getImageDimensions(file);
    
    // Generate all three sizes in parallel
    const [thumbnail, medium, original] = await Promise.all([
      resizeImage(file, {
        maxWidth: thumbnailSize,
        maxHeight: thumbnailSize,
        quality: IMAGE_CONFIG.THUMBNAIL_QUALITY,  // Mobile-aware thumbnail quality
        format: 'jpeg'
      }),
      resizeImage(file, { 
        maxWidth: mediumSize, 
        maxHeight: mediumSize, 
        quality: quality,
        format: 'jpeg'
      }),
      // Only resize original if it's larger than max size
      originalDimensions.width > originalMaxSize || originalDimensions.height > originalMaxSize
        ? resizeImage(file, { 
            maxWidth: originalMaxSize, 
            maxHeight: originalMaxSize, 
            quality: quality,
            format: 'jpeg'
          })
        : file
    ]);


    return {
      thumbnail,
      medium,
      original,
      metadata: {
        originalWidth: originalDimensions.width,
        originalHeight: originalDimensions.height,
        fileName: file.name,
        mimeType: file.type,
        originalSize: file.size,
        thumbnailSize: thumbnail.size,
        mediumSize: medium.size,
        finalSize: original.size
      }
    };
  } catch (error) {
    console.error('Error creating post image sizes:', error);
    throw error;
  }
}

/**
 * Get image dimensions from a file
 * @param {File} file - The image file
 * @returns {Promise<Object>} Object with width and height
 */
export function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
      URL.revokeObjectURL(img.src);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for dimension detection'));
      URL.revokeObjectURL(img.src);
    };
    
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Validate image file for post uploads
 * @param {File} file - The image file to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result with isValid boolean and error message
 */
export function validatePostImage(file, options = {}) {
  const {
    maxSizeMB = 10,
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    minWidth = 100,
    minHeight = 100
  } = options;

  // Check file size
  if (file.size > maxSizeMB * 1024 * 1024) {
    return {
      isValid: false,
      error: `File size must be less than ${maxSizeMB}MB`
    };
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `File type must be one of: ${allowedTypes.join(', ')}`
    };
  }

  return { isValid: true };
}

/**
 * Create optimized filename for post images
 * @param {string} originalName - Original filename
 * @param {string} size - Size variant ('thumbnail', 'medium', 'original')
 * @param {string} postId - Post ID for organization
 * @returns {string} Optimized filename
 */
export function createPostImageFilename(originalName, size, postId) {
  const timestamp = Date.now();
  const extension = originalName.split('.').pop() || 'jpg';
  return `post-${postId}-${size}-${timestamp}.${extension}`;
}